import React, { useMemo, useState } from "react";
import classNames from "classnames";
import { ITabReport } from "redux/app/logs/@types";

interface IDeleteRecordFormProps {
    report: ITabReport;
    onClose: () => void;
    onSubmit: (data: any) => void;
}

const DeleteRecordForm: React.FC<IDeleteRecordFormProps> = ({
    report,
    onClose,
    onSubmit,
}) => {
    const [confirmDelete, setConfirmDelete] = useState(false);

    const hasTimelineReports = (report.timeline || []).length > 0;
    const hasStandaloneReports = useMemo(() => {
        const standaloneForm = report?.standaloneForm;
        if (!standaloneForm || typeof standaloneForm !== "object") return false;

        return Object.values(standaloneForm).some((value) => {
            if (!value) return false;
            if (Array.isArray(value)) return value.length > 0;
            if (typeof value === "object") return Object.keys(value).length > 0;
            return true;
        });
    }, [report?.standaloneForm]);

    const canDeleteRecord = !hasTimelineReports && !hasStandaloneReports;

    const handleSubmit = () => {
        if (!canDeleteRecord || !confirmDelete) return;
        onSubmit({
            backup: report,
        });
    };

    return (
        <div className="p-4">
            <h3 className="mb-4 text-lg font-medium text-gray-900">Delete Record</h3>

            {!canDeleteRecord && (
                <div className="p-3 mb-4 text-sm text-red-700 bg-red-100 rounded-md">
                    This record cannot be deleted yet. Please delete all timeline and standalone reports first.
                </div>
            )}

            <div className="p-3 mb-4 text-sm text-yellow-700 bg-yellow-100 rounded-md">
                This action will delete this CNR record with selected date {report?.selectedDate?.split("T")?.[0]}
            </div>

            <label className="flex items-start mb-4">
                <input
                    type="checkbox"
                    className="w-4 h-4 mt-0.5 text-red-600 border-gray-300 rounded"
                    checked={confirmDelete}
                    onChange={(e) => setConfirmDelete(e.target.checked)}
                    disabled={!canDeleteRecord}
                />
                <span className="ml-2 text-sm text-gray-700">
                    I confirm this CNR record has no timeline or standalone reports and should be deleted.
                </span>
            </label>

            <div className="flex justify-end gap-3">
                <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                    Cancel
                </button>
                <button
                    onClick={handleSubmit}
                    disabled={!canDeleteRecord || !confirmDelete}
                    className={classNames(
                        "px-4 py-2 text-sm font-medium text-white rounded-md",
                        canDeleteRecord && confirmDelete
                            ? "bg-red-600 hover:bg-red-700"
                            : "bg-gray-300 cursor-not-allowed"
                    )}
                >
                    Delete Record
                </button>
            </div>
        </div>
    );
};

export default DeleteRecordForm;
